import React, { createContext, useContext, useEffect, useState } from 'react';
import { ControllerState } from 'types/react';

import Dock from '.';
import {
  loadDesktopItems,
  loadDockItems,
  saveDesktopItems,
  saveDockItems,
} from './localStorage';

type DockContextType = {
  dockItems: ControllerState<DockItemType[]>;
  desktopItems: ControllerState<DesktopItemType[]>;
};

const DockContext = createContext<DockContextType | null>(null);

type DockItemTypeBase = {
  image?: string;
  label: string;
};

type DockItemTypeLink = DockItemTypeBase & {
  type: 'link';
};

type DockItemTypeEntity = DockItemTypeBase & {
  type: 'entity';
  entityId: string;
  workspaceId: string;
};

export type DockItemType = DockItemTypeLink | DockItemTypeEntity;

export type DesktopItemType = DockItemType & {
  position?: number;
};

export function DockProvider(props: React.PropsWithChildren) {
  const [dockItems, setDockItems] = useState<DockItemType[]>(loadDockItems);
  const [desktopItems, setDesktopItems] =
    useState<DesktopItemType[]>(loadDesktopItems);

  // Save to localStorage whenever dockItems or desktopItems change
  useEffect(() => {
    saveDockItems(dockItems);
  }, [dockItems]);

  useEffect(() => {
    saveDesktopItems(desktopItems);
  }, [desktopItems]);

  return (
    <DockContext.Provider
      value={{
        dockItems: { current: dockItems, onChange: setDockItems },
        desktopItems: { current: desktopItems, onChange: setDesktopItems },
      }}
    >
      {props.children}
      <Dock />
    </DockContext.Provider>
  );
}

export function useDock() {
  const context = useContext(DockContext);
  if (!context) {
    throw new Error('useDock must be used within a DockProvider');
  }

  return {
    dockItems: context.dockItems,
    desktopItems: context.desktopItems,
  };
}
